import { useState } from "react";
import { Text } from "@mantine/core";
import { openConfirmModal } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useOrderWorkflow } from "@jasper/product-workflows";
import {
  hasGraphQLErrorCode,
  useOrderConfiguration,
  useRequestOrderDesign,
} from "@jasper/use-cases";
import { isExpressDate } from "./delivery.utils";

const DESIGN_EXPRESS_MESSAGE =
  "La demande d'un design ne permet pas de bénéficier d'un délai réduit.";

const openDesignExpressConfirmModal = ({
  consequence,
  onConfirm,
}: {
  consequence: string;
  onConfirm: () => void;
}) =>
  openConfirmModal({
    title: "Demande de confirmation",
    children: (
      <Text>
        {DESIGN_EXPRESS_MESSAGE} {consequence}
      </Text>
    ),
    labels: { confirm: "Confirmer", cancel: "Annuler" },
    onConfirm,
  });

// Everything needed to send the order to the lab, with the same rules as V1:
// - a design request can't be combined with an express date, switching from one to the other asks for confirmation
// - the backend can refuse a delivery date that became too close, the user must then pick another one
// - the design request is only sent once the order is confirmed
// - once sent, the user moves on to the confirmation page
export const useOrderSubmission = ({
  orderId,
  confirmOrder,
  deliveryDate,
  setDeliveryDate,
  estimatedOptimalDeliveryDate,
}: {
  orderId: string;
  confirmOrder: ReturnType<typeof useOrderConfiguration>["confirmOrder"];
  deliveryDate?: Date;
  setDeliveryDate: (date: Date) => void;
  estimatedOptimalDeliveryDate?: Date;
}) => {
  const isDesignRequestEnabled = !!useFeatureFlagEnabled(
    "order_design_request"
  );
  const { requestOrderDesign } = useRequestOrderDesign();
  const { goNext } = useOrderWorkflow({ orderId });

  const [isDesignRequested, setIsDesignRequested] = useState(false);
  const [isDeliveryDateTooSoon, setIsDeliveryDateTooSoon] = useState(false);
  const [isSending, setIsSending] = useState(false);

  // Every accepted date change also clears the "date too soon" error returned by a previous send
  const applyDeliveryDate = (newDeliveryDate: Date) => {
    setDeliveryDate(newDeliveryDate);
    setIsDeliveryDateTooSoon(false);
  };

  // Requesting a design while an express date is selected moves the date back to the optimal one
  const toggleDesignRequest = () => {
    if (isDesignRequested) {
      setIsDesignRequested(false);
      return;
    }

    if (
      deliveryDate &&
      estimatedOptimalDeliveryDate &&
      isExpressDate(deliveryDate, estimatedOptimalDeliveryDate)
    ) {
      openDesignExpressConfirmModal({
        consequence:
          "En confirmant, la date de livraison sera mise à jour sur la date optimale.",
        onConfirm: () => {
          applyDeliveryDate(estimatedOptimalDeliveryDate);
          setIsDesignRequested(true);
        },
      });
      return;
    }

    setIsDesignRequested(true);
  };

  // Picking an express date while a design is requested cancels the design request
  const changeDeliveryDate = (newDeliveryDate: Date) => {
    if (
      isDesignRequested &&
      isExpressDate(newDeliveryDate, estimatedOptimalDeliveryDate)
    ) {
      openDesignExpressConfirmModal({
        consequence:
          "En confirmant, la demande de design sera annulée et la date de livraison mise à jour.",
        onConfirm: () => {
          setIsDesignRequested(false);
          applyDeliveryDate(newDeliveryDate);
        },
      });
      return;
    }

    applyDeliveryDate(newDeliveryDate);
  };

  const send = async () => {
    if (!deliveryDate || !estimatedOptimalDeliveryDate || isSending) {
      return;
    }

    setIsSending(true);
    try {
      await confirmOrder({
        deliveryDate,
        isRush: isExpressDate(deliveryDate, estimatedOptimalDeliveryDate),
      });
    } catch (error) {
      if (hasGraphQLErrorCode(error, "ORDER_DELIVERY_DATE_TOO_SOON")) {
        setIsDeliveryDateTooSoon(true);
      } else {
        console.error("Error while confirming the order", error);
        notifications.show({
          color: "red",
          title: "Envoi impossible",
          message:
            "La commande n'a pas pu être envoyée au laboratoire. Veuillez réessayer.",
        });
      }
      return;
    } finally {
      setIsSending(false);
    }

    // The order is already sent at this point, a failed design request must not be reported as a failed send
    if (isDesignRequested) {
      await requestOrderDesign(orderId).catch(error => {
        console.error("Error while requesting order design", error);
        notifications.show({
          color: "red",
          title: "Demande de design non enregistrée",
          message:
            "La commande a bien été envoyée au laboratoire, mais la demande de design n'a pas pu être enregistrée.",
        });
      });
    }

    goNext();
  };

  return {
    isDesignRequestEnabled,
    isDesignRequested,
    toggleDesignRequest,
    changeDeliveryDate,
    isDeliveryDateTooSoon,
    isSending,
    send,
  };
};
